import { useState, useRef, useEffect } from 'react';
import { ArrowDown, ArrowUp, Spinner } from '../../assets/icon/svg';
import { DropdownOption } from '../../features/auth/auth-state';

interface Props {
    value: DropdownOption | null;
    options: DropdownOption[];
    onChange: (value: DropdownOption) => void;
    width?: string;
    padding?: string;
    open: boolean;
    setOpen: (value: boolean) => void;
    isLoading?: boolean;
    isSubTitle?: boolean;
    isDisable?: boolean;
    isNoBorder?: boolean;
    isShowArrow?: boolean;
    minH?: string;
}

export default function ButtonDropdownOption({ minH = 'min-h-[34px] ', isShowArrow = true, isDisable, isNoBorder = false, isSubTitle = false, isLoading, open, setOpen, value, onChange, options, width = 'w-1/3', padding = 'px-3 py-2' }: Props) {
    
    const selected = value || null;
    const [dropUp, setDropUp] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, setOpen]);

    useEffect(() => {
        if (!open || !buttonRef.current) return;

        const calculatePosition = () => {
            const rect = buttonRef.current!.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const dropdownHeight = Math.min(options.length * 42, 200);

            if (spaceBelow < dropdownHeight + 10 && spaceAbove > spaceBelow) {
                setDropUp(true);
            } else {
                setDropUp(false);
            }
        };

        calculatePosition();

        window.addEventListener('scroll', calculatePosition);
        window.addEventListener('resize', calculatePosition);

        return () => {
            window.removeEventListener('scroll', calculatePosition);
            window.removeEventListener('resize', calculatePosition);
        };
    }, [open, options.length]);

    const getButtonStyle = () => {
        if (isNoBorder) {
            return 'bg-gray-100 rounded-md border border-gray-300';
        }

        if (open) {
            return 'rounded-md border border-[#186cb2] bg-white ring-1 ring-[#186cb2]/20';
        }

        if (value?.name === 'None' || value?.name === 'Select') {
            return 'rounded-md border border-gray-300 bg-white';
        }

        return 'rounded-md border border-[#186cb2]/30 bg-[#186cb2]/10';
    };

    return (
        <div ref={dropdownRef} className={`relative ${width}`}>
            <button
                disabled={isDisable}
                ref={buttonRef}
                type="button"
                className={`disabled:cursor-not-allowed w-full ${minH} text-xs flex justify-between items-center ${getButtonStyle()} ${padding}`}
                onClick={() => setOpen(!open)}
            >
                <span className={`flex-1 text-left truncate ${isShowArrow ? 'mr-2' : ''}`}>
                    {selected?.name}
                </span>
                {isShowArrow && (
                    <span className="flex items-center justify-center w-4 h-4 flex-shrink-0 text-gray-500">
                        {open ? <ArrowUp /> : <ArrowDown />}
                    </span>
                )}
            </button>
            {open && isShowArrow && (
                <div
                    className={`absolute z-50 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-40 overflow-y-auto 
                        ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                    style={{
                        bottom: dropUp ? '100%' : undefined,
                        top: dropUp ? undefined : '100%',
                        marginTop: dropUp ? '0px' : '5px',
                        marginBottom: dropUp ? '5px' : '0px',
                    }}
                >
                    {isLoading ? (
                        <div className="p-3 flex items-center justify-center h-auto w-full">
                            <Spinner color="#186cb2" size={20} />
                        </div>
                    ) : (
                        options.map((option) => (
                            <div
                                className="px-3 py-2 flex flex-col hover:bg-[#186cb2]/5 cursor-pointer transition-colors"
                                key={option.id}
                                onClick={() => {
                                    onChange(option);
                                    setOpen(false);
                                }}
                            >
                                <div className="text-xs text-gray-700">
                                    {option.name}
                                </div>
                                {isSubTitle && (
                                    <div className="text-[10px] text-gray-500">
                                        {option.subTitle}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}